import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { saveAdditionalNotesTemplate, updateAdditionalNotesTemplate } from "@/lib/stores/additionalNotesTemplates";
import { useAuthStore } from "@/lib/auth";
import { getDoctorIdForUser, ensureDoctorProfile } from "@/lib/utils/doctorUtils";
import { AdditionalNotesTemplate } from "@/types";

interface SaveAdditionalNotesTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  notes: string;
  onTemplateSaved?: () => void;
  editingTemplate?: AdditionalNotesTemplate | null; // Template being edited
}

export function SaveAdditionalNotesTemplateDialog({
  isOpen,
  onClose,
  notes: initialNotes,
  onTemplateSaved,
  editingTemplate = null
}: SaveAdditionalNotesTemplateDialogProps) {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const [disease, setDisease] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [notes, setNotes] = useState(initialNotes);
  const [isSaving, setIsSaving] = useState(false);

  // Update form when dialog opens or when editing template changes
  useEffect(() => {
    if (isOpen) {
      if (editingTemplate) {
        // Pre-fill form with template data
        setDisease(editingTemplate.disease || "");
        setTemplateName(editingTemplate.templateName || "");
        setNotes(editingTemplate.notes || "");
      } else {
        // Reset form for new template
        setDisease("");
        setTemplateName("");
        setNotes(initialNotes);
      }
    }
  }, [isOpen, editingTemplate, initialNotes]);

  const handleSave = async () => {
    if (!disease.trim()) {
      toast({
        title: "Disease required",
        description: "Please enter a disease for the template",
        variant: "destructive"
      });
      return;
    }

    if (!templateName.trim()) {
      toast({
        title: "Template name required",
        description: "Please enter a name for the template",
        variant: "destructive"
      });
      return;
    }

    if (!notes.trim()) {
      toast({
        title: "Notes required",
        description: "Please enter some notes to save as template",
        variant: "destructive"
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Authentication error",
        description: "User ID not found",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      // Get or create doctor profile for the user
      let doctorId = await getDoctorIdForUser(user);
      
      if (!doctorId) {
        console.log('No doctor profile found, creating one...');
        doctorId = await ensureDoctorProfile(user);
        
        if (!doctorId) {
          toast({
            title: "Error",
            description: "Could not create doctor profile. Please contact administrator.",
            variant: "destructive"
          });
          return;
        }
      }

      console.log('Using doctor ID:', doctorId);

      if (editingTemplate) {
        // Update existing template
        await updateAdditionalNotesTemplate(editingTemplate.id, {
          disease: disease.trim(),
          templateName: templateName.trim(),
          notes: notes.trim()
        });

        toast({
          title: "Template updated",
          description: `Template "${templateName}" updated successfully`
        });
      } else {
        // Create new template
        await saveAdditionalNotesTemplate({
          doctorId: doctorId,
          disease: disease.trim(),
          templateName: templateName.trim(),
          notes: notes.trim()
        });

        toast({
          title: "Template saved",
          description: `Template "${templateName}" saved successfully for ${disease}`
        });
      }

      // Reset form
      setDisease("");
      setTemplateName("");
      setNotes("");
      onTemplateSaved?.();
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error saving template",
        description: error instanceof Error ? error.message : "Failed to save template. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setDisease("");
    setTemplateName("");
    setNotes("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingTemplate ? "Edit Template" : "Save Notes as Template"}
          </DialogTitle>
          <DialogDescription>
            {editingTemplate
              ? "Update the additional notes template details below."
              : "Save your current additional notes as a reusable template for future prescriptions with similar diseases."}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left side - Disease and Template Name */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="disease">Disease/Condition</Label>
              <Input
                id="disease"
                value={disease}
                onChange={(e) => setDisease(e.target.value)}
                placeholder="e.g., ASOM, SINUSITIS, ADULT ACUTE TONSILLITIS"
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Standard ASOM Notes"
                className="w-full"
              />
            </div>

            <div className="text-sm text-muted-foreground">
              <p>This template will be saved to your personal template library and can be used for future patients with similar diseases.</p>
            </div>
          </div>

          {/* Right side - Notes Editor/Preview */}
          <div className="space-y-4">
            <Label>Notes {editingTemplate ? "(Editable)" : "to Save"}</Label>
            {editingTemplate ? (
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter additional notes..."
                className="min-h-[200px]"
              />
            ) : (
              <div className="border rounded-md p-4 max-h-96 overflow-y-auto">
                {!notes.trim() ? (
                  <p className="text-sm text-muted-foreground">No notes to save</p>
                ) : (
                  <div className="whitespace-pre-wrap text-sm">
                    {notes}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving || !notes.trim()}
            className="min-w-[120px]"
          >
            {isSaving ? (editingTemplate ? "Updating..." : "Saving...") : (editingTemplate ? "Update Template" : "Save Template")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
